/**
 * Home Feed Query Hooks
 *
 * TanStack Query hooks for home page data fetching.
 * All paginated sections use useInfiniteQuery to support scroll-to-load.
 * Hooks accept `initialData` from server components to seed the cache,
 * enabling instant repeat-visit rendering via stale-while-revalidate.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { homeClient } from '../client/home.client';
import { playlistsClient } from '../client/playlists.client';
import { queryKeys } from '../query-keys';
import { seededShuffle, getDailySeed } from '@/shared/utils/array';
import type { HomePageData } from '@/shared/lib/api/home-data';
import type { PaginatedResponse } from '@/types/api';
import type { Banner, Video, Series, Creator, Playlist } from '@/types';

const TRENDING_MAX_PAGES = 5;

/** 5 minutes — shared staleTime for all home infinite queries */
const HOME_STALE_TIME = 5 * 60 * 1000;

/** Shared getNextPageParam for all PaginatedResponse-based infinite queries */
function getNextPageParam<T>(lastPage: PaginatedResponse<T>): number | undefined {
  if (lastPage.current_page < lastPage.last_page) {
    return lastPage.current_page + 1;
  }
  return undefined;
}

/** Build initialData for useInfiniteQuery from a server-fetched PaginatedResponse */
function makeInfiniteInitialData<T>(page: PaginatedResponse<T>) {
  return {
    initialData: {
      pages: [page],
      pageParams: [1 as number],
    },
    initialDataUpdatedAt: Date.now(),
  };
}

export function useTrendingPlaylistVideos(options: {
  playlistId: number | null;
  initialData?: PaginatedResponse<Video>;
}) {
  const { playlistId, initialData } = options;
  return useInfiniteQuery({
    queryKey: queryKeys.playlists.detail(playlistId ?? 'none'),
    queryFn: async ({ pageParam }) => {
      if (playlistId == null) {
        throw new Error('Trending playlist id missing');
      }
      const detail = await playlistsClient.get(playlistId, pageParam);
      return detail.videos;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (allPages.length >= TRENDING_MAX_PAGES) return undefined;
      if (lastPage.current_page < lastPage.last_page) return lastPage.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
    staleTime: HOME_STALE_TIME,
    enabled: playlistId != null,
    ...(initialData ? makeInfiniteInitialData(initialData) : {}),
  });
}

export function useBanners(options: { initialData?: Banner[] } = {}) {
  return useQuery({
    queryKey: queryKeys.home.banners(),
    queryFn: () => homeClient.getBanners(),
    ...(options.initialData && {
      initialData: options.initialData,
      initialDataUpdatedAt: Date.now(),
    }),
  });
}

export function useFeaturedVideos(options: { initialData?: PaginatedResponse<Video> } = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.home.featured(),
    queryFn: ({ pageParam }) => homeClient.getFeaturedVideos(undefined, pageParam),
    getNextPageParam,
    initialPageParam: 1,
    staleTime: HOME_STALE_TIME,
    ...(options.initialData ? makeInfiniteInitialData(options.initialData) : {}),
  });
}

export function useRecommendedVideos(options: { initialData?: PaginatedResponse<Video> } = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.home.recommended(),
    queryFn: ({ pageParam }) => homeClient.getRecommendedVideos(undefined, pageParam),
    getNextPageParam,
    initialPageParam: 1,
    staleTime: HOME_STALE_TIME,
    ...(options.initialData ? makeInfiniteInitialData(options.initialData) : {}),
  });
}

export function useShortVideos(options: { initialData?: PaginatedResponse<Video> } = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.home.shorts(),
    queryFn: ({ pageParam }) => homeClient.getShortVideosV2(undefined, pageParam),
    getNextPageParam,
    initialPageParam: 1,
    staleTime: HOME_STALE_TIME,
    ...(options.initialData ? makeInfiniteInitialData(options.initialData) : {}),
  });
}

export function useSeries(options: { initialData?: PaginatedResponse<Series> } = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.home.series(),
    queryFn: ({ pageParam }) => homeClient.getSeries(undefined, pageParam),
    getNextPageParam,
    initialPageParam: 1,
    staleTime: HOME_STALE_TIME,
    ...(options.initialData ? makeInfiniteInitialData(options.initialData) : {}),
  });
}

export function useCourses() {
  return useQuery({
    queryKey: queryKeys.home.courses(),
    queryFn: () => homeClient.getCourses(),
  });
}

export function useCreators(options: { initialData?: PaginatedResponse<Creator> } = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.home.creators(),
    queryFn: async ({ pageParam }) => {
      const page = await homeClient.getCreators(undefined, pageParam);
      // Only shuffle page 1; subsequent pages are appended in API order
      if (pageParam === 1) {
        page.data = seededShuffle(
          page.data.sort((a, b) => a.id - b.id),
          getDailySeed()
        );
      }
      return page;
    },
    getNextPageParam,
    initialPageParam: 1,
    staleTime: HOME_STALE_TIME,
    ...(options.initialData ? makeInfiniteInitialData(options.initialData) : {}),
  });
}

/**
 * Fetch playlists from the API route
 */
async function fetchHomePlaylists(cursor: number | null): Promise<HomePageData> {
  const url = cursor ? `/api/home/playlists?cursor=${cursor}` : '/api/home/playlists?cursor=1';
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch playlists');
  }
  return response.json();
}

export interface HomePlaylistsInfiniteOptions {
  initialPlaylists?: Playlist[];
  initialCursor?: number | null;
  isInitialLastPage?: boolean;
}

/**
 * Hook to fetch home playlists with infinite scroll
 *
 * Uses cursor-based pagination via the /api/home/playlists route
 */
export function useHomePlaylistsInfinite(options: HomePlaylistsInfiniteOptions = {}) {
  const { initialPlaylists, initialCursor, isInitialLastPage } = options;

  return useInfiniteQuery({
    queryKey: queryKeys.home.playlists(),
    queryFn: ({ pageParam }) => fetchHomePlaylists(pageParam),
    getNextPageParam: (lastPage) => (lastPage.isLastPage ? undefined : lastPage.nextCursor),
    initialPageParam: 1 as number | null,
    ...(initialPlaylists && initialPlaylists.length > 0
      ? {
          initialData: {
            pages: [
              {
                playlists: initialPlaylists,
                nextCursor: initialCursor ?? null,
                isLastPage: isInitialLastPage ?? false,
              } as HomePageData,
            ],
            pageParams: [1 as number | null],
          },
        }
      : {}),
  });
}
